import type { Action, ThunkDispatch } from "@reduxjs/toolkit";

import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { subscribeToDishes } from "@dashboard/store/actions/dishActions";
import { subscribeToLocations } from "@dashboard/store/actions/locationActions";
import { subscribeToMenus } from "@dashboard/store/actions/menuActions";
import { subscribeToMenuUrlsV2 } from "@dashboard/store/actions/menuUrlV2Actions";
import { subscribeToProducts } from "@dashboard/store/actions/productActions";
import type { ReduxState } from "@dashboard/types/reduxState";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import { useRestaurant } from "./useRestaurant";

type SyncThunkDispatch = ThunkDispatch<ReduxState, ThunkExtraArgument, Action>;

/**
 * Keeps the Redux product, dish, menu, location and menu URL state in sync
 * with Firestore for the selected restaurant while the calling component is
 * mounted. Each effect cleanup unsubscribes on unmount and before
 * re-subscribing when the restaurant changes.
 */
export const useSyncState = () => {
  const dispatch: SyncThunkDispatch = useDispatch();
  const { uuid } = useRestaurant();

  useEffect(() => dispatch(subscribeToProducts(uuid)), [dispatch, uuid]);
  useEffect(() => dispatch(subscribeToDishes(uuid)), [dispatch, uuid]);
  useEffect(() => dispatch(subscribeToMenus(uuid)), [dispatch, uuid]);
  // This is needed to show the LocationsComponent inside the MenuDrawer.
  useEffect(() => dispatch(subscribeToLocations(uuid)), [dispatch, uuid]);
  // This is needed for the v2 preview button in the header.
  useEffect(() => dispatch(subscribeToMenuUrlsV2(uuid)), [dispatch, uuid]);
};
